import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { withFormik } from 'formik';
import { MobileInput } from '../../components/elements';

const LoginIconBox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: #fff0e6;
  border-radius: 12px;
  font-size: 24px;
  margin-bottom: 1.25rem;
`;

const LoginHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const LoginSubtext = styled.p`
  font-size: 0.875rem;
  color: #6b6b6b;
  line-height: 1.6;
  text-align: left;
  margin-bottom: 1.75rem;
`;

const FieldGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  font-size: 15px;
  color: #1a1a1a;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &:focus {
    border-color: #e8792c;
    box-shadow: 0 0 0 3px rgba(232, 121, 44, 0.12);
  }

  &::placeholder {
    color: #bbb;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const EyeToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 15px;

  &:hover {
    color: #555;
  }
`;

const SignInButton = styled.button`
  &&& {
    border-radius: 10px;
    height: 50px;
    font-size: 15px;
    font-weight: 600;
    margin-top: 0.25rem;

    @media (max-width: 480px) {
      height: 46px;
      font-size: 14px;
    }
  }
`;

const ErrorText = styled.p`
  color: #e05555;
  font-size: 13px;
  margin-top: 6px;
`;

const PhoneFormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const PhoneErrorMsg = styled.p`
  color: #e05555;
  font-size: 13px;
  margin-top: 0.5rem;
  min-height: 16px;
`;

const SetupHeading = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 0.4rem;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 1.15rem;
  }
`;

const SetupSubtext = styled.p`
  font-size: 13.5px;
  color: #6b7280;
  line-height: 1.65;
  text-align: left;
  margin-bottom: 18px;
`;

const SuccessIconCircle = styled.div`
  width: 60px;
  height: 60px;
  background: #ecfdf5;
  border: 2px solid rgba(5, 150, 105, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin: 0 auto 18px;
  animation: popIn 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes popIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 6px;
  text-align: center;
`;

const SuccessDesc = styled.p`
  font-size: 13.5px;
  color: #6b7280;
  line-height: 1.65;
  text-align: center;
`;

const ProgressBarWrap = styled.div`
  height: 3px;
  background: #f0f1f3;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 24px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #00b28c;
  border-radius: 2px;
  animation: fillBar 2.5s ease forwards;

  @keyframes fillBar {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
`;

const ProgressHint = styled.p`
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin-top: 10px;
`;

const DeviceIconBox = styled.div`
  width: 60px;
  height: 60px;
  background: #eff6ff;
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin: 0 auto 18px;
  animation: popIn 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes popIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const RememberDeviceNote = styled.p`
  font-size: 11.5px;
  color: #9ca3af;
  text-align: center;
  margin-top: 1rem;
  line-height: 1.5;
`;

const OtpIconBox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: #fff0e6;
  border-radius: 12px;
  font-size: 24px;
  margin-bottom: 1.25rem;
`;

const OtpHead = styled.div`
  margin-bottom: 1.25rem;
`;

const OtpHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.4rem;
  text-align: start;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const OtpSubtext = styled.p`
  font-size: 0.875rem;
  color: #6b6b6b;
  line-height: 1.6;
  text-align: start;
`;

const OtpPhoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f9f9f9;
  border: 1.5px solid #ececec;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 1.5rem;
`;

const OtpPhoneIconBox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: #fff0e6;
  border-radius: 7px;
  font-size: 16px;
  flex-shrink: 0;
`;

const OtpPhoneText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  letter-spacing: 0.5px;
`;

const VerifyButton = styled.button`
  &&& {
    border-radius: 10px;
    height: 50px;
    font-size: 15px;
    font-weight: 600;
    background: #6b7280;
    border-color: #6b7280;
    color: #fff;

    &:hover:not([disabled]) {
      background: #4b5563;
      border-color: #4b5563;
    }

    @media (max-width: 480px) {
      height: 46px;
      font-size: 14px;
    }
  }
`;

const OtpBoxes = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 1.25rem;

  @media (max-width: 380px) {
    gap: 6px;
  }
`;

const OtpBox = styled.input`
  flex: 1;
  min-width: 0;
  height: 56px;
  border: none;
  border-radius: 10px;
  background: #f0f0f0;
  font-size: 22px;
  font-weight: 500;
  color: #1e1e1e;
  text-align: center;
  outline: none;
  caret-color: transparent;
  transition: background 0.15s, box-shadow 0.15s;

  &:focus {
    background: #e8e8e8;
    box-shadow: 0 0 0 3px rgba(232, 121, 44, 0.18);
  }

  &.has-val {
    background: #e8e8e8;
  }

  &.otp-err {
    background: #fff0f0;
    box-shadow: 0 0 0 2px #e05555;
  }

  @media (max-width: 480px) {
    height: 46px;
    font-size: 18px;
    border-radius: 8px;
  }

  @media (max-width: 360px) {
    height: 40px;
    font-size: 16px;
  }
`;

const OtpHint = styled.div`
  font-size: 12px;
  min-height: 16px;
  margin-bottom: 1rem;

  &[data-type='bad'] {
    color: #e05555;
  }

  &[data-type='good'] {
    color: #27a76a;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  color: #e8792c;
  cursor: pointer;
  font-weight: 600;
  padding: 0;

  &:disabled {
    color: #bbb;
    cursor: not-allowed;
  }
`;

const getLast3Digits = phone => (phone || '').replace(/\D/g, '').slice(-3);

const getMaskedPhone = phone => {
  const p = phone || '';
  const match = p.match(/^(\+\d{1,3})\s?(.+)$/);
  if (match) {
    const countryCode = match[1];
    const local = match[2].replace(/\D/g, '');
    return `${countryCode} ${local[0] || ''}•• ••• ${local.slice(-3)}`;
  }
  const digits = p.replace(/\D/g, '');
  return `${digits[0] || ''}•• ••• ${digits.slice(-3)}`;
};

const Form = ({
  values,
  handleBlur,
  handleChange,
  errors,
  touched,
  handleSubmit,
  isError,
  isLoading,
  otpRequired,
  onOtpSubmit,
  onBack,
  onResend,
  authenticationProfile,
  phoneRequired,
  onPhoneSubmit,
  userWithoutPhoneLogin,
  redirecting,
  rememberDevicePrompt,
  onRememberDevice,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const [otpDigits, setOtpDigits] = useState(Array(6).fill(''));
  const [otpHasError, setOtpHasError] = useState(false);
  const [hint, setHint] = useState({ text: '', type: '' });
  const [countdown, setCountdown] = useState(300);
  const [timerKey, setTimerKey] = useState(0);
  const [disableResendButton, setDisableResendButton] = useState(false);
  const boxRefs = useRef([]);

  useEffect(() => {
    if (!otpRequired) return () => {};

    setCountdown(300);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpRequired, timerKey]);

  useEffect(() => {
    if (otpRequired) {
      boxRefs.current[0]?.focus();
    } else {
      setOtpDigits(Array(6).fill(''));
      setOtpHasError(false);
      setHint({ text: '', type: '' });
    }
  }, [otpRequired]);

  useEffect(() => {
    if (isError && isError.error) {
      setOtpHasError(true);
      setHint({ text: isError.message, type: 'bad' });
    } else {
      setOtpHasError(false);
      setHint({ text: '', type: '' });
    }
  }, [isError]);

  const formatCountdown = secs => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleBoxKeyDown = (e, idx) => {
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const newDigits = [...otpDigits];
      newDigits[idx] = e.key;
      setOtpDigits(newDigits);
      setOtpHasError(false);
      setHint({ text: '', type: '' });
      if (idx < 5) boxRefs.current[idx + 1].focus();
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (otpDigits[idx]) {
        const newDigits = [...otpDigits];
        newDigits[idx] = '';
        setOtpDigits(newDigits);
      } else if (idx > 0) {
        const newDigits = [...otpDigits];
        newDigits[idx - 1] = '';
        setOtpDigits(newDigits);
        boxRefs.current[idx - 1].focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      boxRefs.current[idx - 1].focus();
      return;
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      boxRefs.current[idx + 1].focus();
      return;
    }
    if (e.key === 'Enter') {
      const code = otpDigits.join('');
      if (code.length === 6 && !isLoading) {
        onOtpSubmit(code);
      }
    }
  };

  const handleBoxPaste = e => {
    e.preventDefault();
    setHint({ text: '', type: '' });
    const pasted = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/[^0-9]/g, '');
    const newDigits = Array(6).fill('');
    pasted
      .split('')
      .slice(0, 6)
      .forEach((c, j) => {
        newDigits[j] = c;
      });
    setOtpDigits(newDigits);
    boxRefs.current[Math.min(pasted.length, 5)].focus();
  };

  const handleResend = async () => {
    setOtpDigits(Array(6).fill(''));
    setOtpHasError(false);
    setDisableResendButton(true);

    const success = await onResend();
    if (success) {
      setHint({ text: 'A new code has been sent.', type: 'good' });
      setTimerKey(k => k + 1);
      setTimeout(() => boxRefs.current[0]?.focus(), 0);
    }
    setDisableResendButton(false);
  };

  const handlePhoneSend = () => {
    setPhoneError('');
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }
    if (phoneNumber.replace(/\D/g, '').length < 10) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    onPhoneSubmit(phoneNumber);
  };

  if (redirecting) {
    return (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <SuccessIconCircle>✓</SuccessIconCircle>
        <SuccessTitle>You&apos;re in!</SuccessTitle>
        <SuccessDesc>
          Authentication successful. Redirecting to the KRAVEiN Admin
          Dashboard&hellip;
        </SuccessDesc>
        <ProgressBarWrap>
          <ProgressFill />
        </ProgressBarWrap>
        <ProgressHint>Establishing secure session</ProgressHint>
      </div>
    );
  }

  if (rememberDevicePrompt) {
    return (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <DeviceIconBox>
          <span role="img" aria-label="laptop">
            💻
          </span>
        </DeviceIconBox>
        <SuccessTitle>Remember this device?</SuccessTitle>
        <SuccessDesc>
          Skip the OTP step for 30 days on this browser. Only choose this on a
          trusted, personal device.
        </SuccessDesc>
        <button
          className={`button is-primary is-fullwidth${isLoading ? ' is-loading' : ''}`}
          type="button"
          disabled={isLoading}
          style={{
            borderRadius: '10px',
            height: '50px',
            fontSize: '15px',
            fontWeight: '600',
            marginTop: '1.5rem',
            marginBottom: '0.75rem',
          }}
          onClick={() => onRememberDevice(true)}
        >
          Yes, remember this device
        </button>
        <button
          className="button is-light is-fullwidth"
          type="button"
          disabled={isLoading}
          style={{
            borderRadius: '10px',
            height: '50px',
            fontSize: '15px',
            fontWeight: '600',
          }}
          onClick={() => onRememberDevice(false)}
        >
          Not now
        </button>
        <RememberDeviceNote>
          You can manage trusted devices from your account settings.
        </RememberDeviceNote>
      </div>
    );
  }

  if (phoneRequired) {
    return (
      <>
        <SetupHeading>Secure your account</SetupHeading>
        <SetupSubtext>
          Admin access requires two-factor authentication. Enter your mobile
          number to receive a one-time code each time you log in.
        </SetupSubtext>
        <form
          onSubmit={e => {
            e.preventDefault();
            handlePhoneSend();
          }}
        >
          <PhoneFormGroup>
            <MobileInput
              label="Mobile Number"
              value={phoneNumber}
              onChange={value => {
                setPhoneNumber(value);
                setPhoneError('');
              }}
            />
            {phoneError && <PhoneErrorMsg>{phoneError}</PhoneErrorMsg>}
          </PhoneFormGroup>

          {isError?.error && <PhoneErrorMsg>{isError.message}</PhoneErrorMsg>}

          <VerifyButton
            className={`button is-fullwidth ${isLoading ? 'is-loading' : ''}`}
            type="submit"
            disabled={!phoneNumber.trim() || isLoading}
          >
            Send Verification Code
          </VerifyButton>

          <button
            className="button is-light is-fullwidth"
            type="button"
            style={{ marginTop: '0.5rem' }}
            onClick={() => {
              setPhoneError('');
              onBack();
            }}
          >
            Back
          </button>
        </form>
      </>
    );
  }

  if (otpRequired) {
    const otp = otpDigits.join('');
    const allFilled = otp.length === 6 && !otpDigits.includes('');

    const otpBoxes = (
      <OtpBoxes>
        {['p0', 'p1', 'p2', 'p3', 'p4', 'p5'].map((boxKey, i) => (
          <OtpBox
            key={boxKey}
            ref={el => {
              boxRefs.current[i] = el;
            }}
            maxLength={1}
            inputMode="numeric"
            pattern="[0-9]"
            value={otpDigits[i]}
            className={`${otpDigits[i] ? 'has-val' : ''} ${
              otpHasError ? 'otp-err' : ''
            }`}
            autoComplete={i === 0 ? 'one-time-code' : undefined}
            autoFocus={i === 0}
            onChange={() => {}}
            onKeyDown={e => handleBoxKeyDown(e, i)}
            onPaste={handleBoxPaste}
            onFocus={e => e.target.select()}
          />
        ))}
      </OtpBoxes>
    );

    if (userWithoutPhoneLogin) {
      return (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (allFilled) {
              onOtpSubmit(otp);
            }
          }}
        >
          <SetupHeading>Enter your code</SetupHeading>
          <SetupSubtext>
            We sent a 6-digit code to{' '}
            <strong style={{ color: '#1a1a2e' }}>
              {authenticationProfile?.phoneNumber}
            </strong>
            . This code expires in{' '}
            <strong style={{ color: '#1a1a2e' }}>5 minutes</strong>.
          </SetupSubtext>

          {otpBoxes}
          <OtpHint data-type={hint.type}>{hint.text}</OtpHint>

          <VerifyButton
            className={`button is-fullwidth ${isLoading ? 'is-loading' : ''}`}
            type="submit"
            disabled={!allFilled || isLoading}
          >
            Verify &amp; Complete Setup
          </VerifyButton>

          <FooterRow>
            <ResendButton
              type="button"
              disabled={disableResendButton || countdown > 0}
              onClick={handleResend}
            >
              {countdown > 0
                ? `Resend code (${formatCountdown(countdown)})`
                : 'Resend code'}
            </ResendButton>
          </FooterRow>

          <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '13px',
                color: '#6b7280',
                cursor: 'pointer',
              }}
              onClick={() => {
                setOtpDigits(Array(6).fill(''));
                onBack();
                setHint({ text: '', type: '' });
              }}
            >
              ← Change number
            </button>
          </div>
        </form>
      );
    }

    return (
      <form
        onSubmit={event => {
          event.preventDefault();
          if (allFilled) {
            onOtpSubmit(otp);
          }
        }}
      >
        <OtpIconBox>
          <span
            role="img"
            aria-label={
              authenticationProfile?.isEmailEnabled ? 'email' : 'mobile phone'
            }
          >
            {authenticationProfile?.isEmailEnabled ? '✉️' : '📱'}
          </span>
        </OtpIconBox>

        <OtpHead>
          <OtpHeading>Verify your identity</OtpHeading>
          {authenticationProfile?.isEmailEnabled ? (
            <OtpSubtext>
              A 6-digit code has been sent to your registered email address. The
              code expires in 5 minutes.
            </OtpSubtext>
          ) : (
            <OtpSubtext>
              We sent a 6-digit code to the number ending in{' '}
              <strong style={{ color: '#1a1a1a' }}>
                {getLast3Digits(authenticationProfile?.phoneNumber)}
              </strong>
              . The code expires in 5 minutes.
            </OtpSubtext>
          )}
        </OtpHead>

        {!authenticationProfile?.isEmailEnabled && (
          <OtpPhoneRow>
            <OtpPhoneIconBox>
              <span role="img" aria-label="mobile phone">
                📱
              </span>
            </OtpPhoneIconBox>
            <OtpPhoneText>
              {getMaskedPhone(authenticationProfile?.phoneNumber)}
            </OtpPhoneText>
          </OtpPhoneRow>
        )}

        {otpBoxes}
        <OtpHint data-type={hint.type}>{hint.text}</OtpHint>

        <VerifyButton
          className={`button is-fullwidth ${isLoading ? 'is-loading' : ''}`}
          type="submit"
          disabled={!allFilled || isLoading}
        >
          Verify &amp; Continue
        </VerifyButton>

        <FooterRow>
          <ResendButton
            type="button"
            disabled={disableResendButton || countdown > 0}
            onClick={handleResend}
          >
            {countdown > 0
              ? `Resend code (${formatCountdown(countdown)})`
              : 'Resend code'}
          </ResendButton>
        </FooterRow>

        <button
          className="button is-light is-fullwidth"
          type="button"
          style={{ marginTop: '0.5rem' }}
          onClick={() => {
            setOtpDigits(Array(6).fill(''));
            onBack();
          }}
        >
          Back
        </button>
      </form>
    );
  }

  return (
    <>
      <LoginIconBox>
        <span role="img" aria-label="key">
          🔑
        </span>
      </LoginIconBox>
      <LoginHeading>Welcome back</LoginHeading>
      <LoginSubtext>
        Sign in to the KRAVEiN Admin Portal. 2FA is required for all admin
        accounts.
      </LoginSubtext>

      <form
        onSubmit={event => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        {isError && <ErrorText>{isError.message}</ErrorText>}

        <FieldGroup>
          <FieldLabel htmlFor="email">Email Address</FieldLabel>
          <StyledInput
            id="email"
            type="email"
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="your@email.com"
            autoComplete="email"
          />
          {errors.email && touched.email && (
            <ErrorText>{errors.email}</ErrorText>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <PasswordWrapper>
            <StyledInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="current-password"
              style={{ paddingRight: '44px' }}
            />
            <EyeToggle
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword(v => !v)}
            >
              <i
                className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
              />
            </EyeToggle>
          </PasswordWrapper>
          {errors.password && touched.password && (
            <ErrorText>{errors.password}</ErrorText>
          )}
        </FieldGroup>

        <SignInButton
          className={`button is-primary is-fullwidth ${
            isLoading ? 'is-loading' : ''
          }`}
          type="submit"
          disabled={isLoading}
        >
          Sign In
        </SignInButton>
      </form>
    </>
  );
};
const LoginForm = withFormik({
  mapPropsToValues: ({ preEnteredEmail, preEnteredPassword }) => ({
    email: preEnteredEmail || '',
    password: preEnteredPassword || '',
  }),

  validate: values => {
    const errors = {};
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = 'Please enter valid email!';
    }
    if (!values.password) {
      errors.password = 'Password is required';
    }
    return errors;
  },

  handleSubmit: (values, { props }) => {
    props.onSubmit(values);
  },

  displayName: 'BasicForm',
})(Form);

export default LoginForm;
